import OrganizationApi from '@/api/OrganizationApi';
import RoleUserApi from '@/api/RoleUserApi';
import { i18n } from '@/i18n';
import router from '@/router/index.router';
import { Bus, useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent } from 'vue';
import useSelfService from './SelfService';

class RoleUserService {

	private get api () { return RoleUserApi }
	private get self () { return useSelfService().self }

	async addAsync (userId: number, organizationRoleId: number) {
		try {
			const { data } = await this.api.add({
				userId,
				organizationRoleId,
			});

			Bus.emit(`OrganizationUserAsideDetailsSetup:${userId}:addUserRole`, data);
			Bus.emit(`OrganizationUserStickerSetup:${userId}:addUserRole`, data);

			return data;
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async deleteAsync (roleUserId: number, userId?: number) {
		try {
			const { data } = await this.api.delete(roleUserId);

			if (userId) {
				Bus.emit(`OrganizationUserAsideDetailsSetup:${userId}:deleteUserRole`, roleUserId);
				Bus.emit(`OrganizationUserStickerSetup:${userId}:deleteUserRole`, roleUserId);
			}

			return data;
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async promptUserSelectAsync (roleId: number, showHiddenUser: boolean, organizationId = this.self.organization?.id, transferCreator = false) {
		if (!organizationId) {
			router.push({ name: 'UserGroupDashboard' });
			return;
		}

		return new Promise<Undef<IOrganizationUserListItemModel[]>>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/shared/SharedSelectUserList.vue')),
				NestedProps: {
					title: transferCreator ? i18n.global.t('user.select.user.single') : i18n.global.t('user.select.user.multiple'),
					excludeRoleId: roleId,
					uniq: transferCreator,
					showHiddenUser,
					transferCreator,
				},
				size: 'sm',
				customClass: 'user-select-modal-wrapper',
				events: {
					'cancel': () => resolve(undefined),
					'add': async (Modal, payload: IOrganizationUserListItemModel[]) => {
						Modal._loader()?.show(i18n.global.t('user.enregistrementdesroles'));

						if (transferCreator && payload?.[0].user) {
							try {
								await OrganizationApi.transferCreator(organizationId, { newCreatorId: payload[0].user.id });
								location.reload();
							}
							catch (e: any) {
								useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
							}
						}
						else {
							await Promise.all(payload.map(async (orgUser) => {
								if (orgUser.user) {
									try {
										await this.api.add({
											userId: orgUser.user?.id,
											organizationRoleId: roleId,
										});
									}
									catch (e: any) {
										useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
									}
								}
							}));
						}

						Modal._loader()?.hide();
						resolve(payload);
						Modal.close();
					},
				},
			});
		});
	}

}

const roleUserServiceSingleton = new RoleUserService();

export default function useRoleUserService () {
	return roleUserServiceSingleton;
}
